import { useCart, useRemoveItem as useRemoveItemCart } from '@framework/cart'
import { useAddItem as useAddItemCart } from '@framework/cart'
import { FC, useState, Dispatch, SetStateAction, useEffect, useMemo } from 'react'
import { Button, Modal } from '@components/ui'
import {
  SelectedOptions,
  productHasMetalOptions,
  getVariantMetalOptionValue,
} from '../helpers'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleQuestion } from '@fortawesome/free-regular-svg-icons'
import TooltipDiamondPage from '@components/yadav/ui/Tooltip/TooltipDiamondPage'
import {
  Product,
  ProductOption,
  ProductSize,
  ProductVariant,
} from '@commerce/types/product'
import usePrice from '@framework/product/use-price'
import ProductOptions from '../ProductOptions'
import s from './ProductDescription.module.css'
import useLocalStorage from 'hooks/useLocalStorage'
import {
  InquireOnlyProductForm,
  PurchaseOptionsView,
} from '@components/yadav/ui'
import { useRouter } from 'next/router'
import calculateBankWirePrice from '@utils/calculate-bank-wire-price'
import useWishlist from '@framework/wishlist/use-wishlist'
import WishlistButton from '@components/wishlist/WishlistButton'
import { useSpring, a } from '@react-spring/web'
import { AppointmentButtons, Text } from '@components/ui'
import cn from 'clsx'
import {
  AsscherDiamond,
  CushionDiamond,
  EmeraldDiamond,
  HeartDiamond,
  MarquiseDiamond,
  OvalDiamond,
  PearDiamond,
  PrincessDiamond,
  RadiantDiamond,
  RoundDiamond,
  SquareRadiantDiamond,
} from '@components/yadav/icons'
import ElongatedCushion from '@components/yadav/icons/diamonds/ElongatedCushion'
import { getMinSize, getPriceDifference, getSizeStep } from './helpers'

const canBeSetWithShapes = {
  round: () => <RoundDiamond width="40" height="40" />,
  square_cushion: () => <CushionDiamond width="40" height="40" />,
  elongated_cushion: () => <ElongatedCushion width="40" height="40" />,
  oval: () => <OvalDiamond width="40" height="40" />,
  pear: () => <PearDiamond width="40" height="40" />,
  princess: () => <PrincessDiamond width="40" height="40" />,
  radiant: () => <RadiantDiamond width="40" height="40" />,
  square_radiant: () => <SquareRadiantDiamond width="40" height="40" />,
  emerald: () => <EmeraldDiamond width="40" height="40" />,
  asscher: () => <AsscherDiamond width="40" height="40" />,
  heart: () => <HeartDiamond width="40" height="40" />,
  marquise: () => <MarquiseDiamond width="40" height="40" />,
}

export interface ISizeContent {
  variantId: string
  size: string
  additional: number
}

interface ProductDescriptionProps {
  product: Product
  productVariant: ProductVariant
  selectedOptions: SelectedOptions
  setSelectedOptions: Dispatch<SetStateAction<SelectedOptions>>
}

const ProductDescription: FC<ProductDescriptionProps> = ({
  product,
  productVariant,
  selectedOptions,
  setSelectedOptions,
}) => {
  const [showTooltipPrice, setShowTooltipPrice] = useState(false)
  const [tooltipPriceInTransition, setTooltipPriceInTransition] =
    useState(false)

  const addItemCart = useAddItemCart()
  const { data } = useCart()
  const [loading, setLoading] = useState(false)
  const [displayModal, setDisplayModal] = useState(false)
  const [displayInquireOnlyModal, setDisplayInquireOnlyModal] = useState(false)
  const [settingSelect, setSettingSelect] = useLocalStorage(
    'setting',
    productVariant
  )
  const [diamondInLocal, setDiamondInLocal] = useState()
  const [loadingReview, setLoadingReview] = useState(false)
  const [showFullDetails, setShowFullDetails] = useState(false)
  const [activeItem, setActiveItem] = useState(false)
  //const [showFullDescription, setShowFullDescription] = useState(false)
  const [cyoType, setCyoType] = useState<string>(() => {
    if (product && product.cyo_type) {
      return product.cyo_type
    } else {
      return 'standalone'
    }
  })
  const [active, setActive] = useState(false)
  const sizeOptions = useMemo(() => {
    return productVariant?.sizes || [];
  }, [productVariant?.sizes]);

  const allSizes = sizeOptions.flatMap((sizeGroup) =>
    sizeGroup.available_sizes.map((size) => ({
      size,
      priceAdjustment: sizeGroup.price_adjustment,
      sizeType: sizeGroup.size_type,
    }))
  )

  const [variantSettings, setVariantSettings] = useState({
    metal: '',
    diam_type: '',
    tcw: '',
    size: '',
  })
  const [firstLoadSettings, setFirstLoadSettings] = useState(false)
  const [initializedSettings, setInitializedSettings] = useState(false)

  const [selectedSizeValue, setSelectedSizeValue] = useState(
    allSizes[0]?.size ?? ''
  )

  const allSizesSorted = productVariant?.sizes
    ?.flatMap((group) => group.available_sizes.map((size) => parseFloat(size)))
    .sort((a, b) => a - b); // Order from smallest to largest

  const isFirstSize = parseFloat(selectedSizeValue) === allSizesSorted?.[0];

  const additionalPriceBySize = 
    /* isFirstSize ? 0 // If it is the first size, it does not add adjustment
    :  */
    parseFloat(
      String(
        productVariant?.sizes?.find((group) =>
          group.available_sizes.some(
            (size) => parseFloat(size) === parseFloat(selectedSizeValue)
          )
        )?.price_adjustment || "0"
      )
    );

  // set first option on each case if it wasn't selected before
  useEffect(() => {
    const getProductOptionLabel = (prod?: ProductOption) => {
      return prod?.values?.at(0)?.label
    }

    const metalOption = productVariant.options.find(
      (op) => op.displayName.toUpperCase() === 'METAL'
    )
    const diamTypeOption = productVariant.options.find(
      (op) => op.displayName.toUpperCase() === 'DIAMOND TYPE'
    )
    const tcwOption = productVariant.options.find(
      (op) => op.displayName.toUpperCase() === 'TOTAL CARAT WEIGHT'
    )

    const metalValue = getProductOptionLabel(metalOption)
    const diamTypeValue = getProductOptionLabel(diamTypeOption)
    const tcwValue = getProductOptionLabel(tcwOption)
    const sizeValue = productVariant?.sizes?.at(0)?.size_min?.toString()

    setVariantSettings({
      metal: metalValue ?? '',
      diam_type: diamTypeValue ?? '',
      tcw: tcwValue ?? '',
      size: sizeValue ?? '',
    })

    setFirstLoadSettings(true)
  }, [productVariant])

  // execute after variant setting has any value on each option
  useEffect(() => {
    if (initializedSettings) {
      const formatedOptions = {
        metal: variantSettings.metal?.toLowerCase() || '',
        'diamond type': variantSettings.diam_type?.toLowerCase() || '',
        'total carat weight': variantSettings.tcw?.toLowerCase() || '',
        size: variantSettings.size?.toLowerCase() || '',
      }

      const formatedOptionsFiltered = Object.fromEntries(
        Object.entries(formatedOptions).filter(
          ([key, value]) => value !== '' && value !== undefined
        )
      )

      if (Object.keys(formatedOptionsFiltered).length > 0)
        setSelectedOptions(formatedOptionsFiltered)
    }

    if (firstLoadSettings) setInitializedSettings(true)
  }, [
    variantSettings,
    firstLoadSettings,
    setSelectedOptions,
    initializedSettings,
  ])

  const router = useRouter()
  const { data: wishList } = useWishlist()

  const formattedSizeContent = JSON.stringify({
    variantId: productVariant.id,
    size: selectedSizeValue,
    additional: additionalPriceBySize,
  })

  const addToCart = async () => {
    if (data) {
      setLoading(true)
      try {
        await addItemCart({
          productId: String(product.id),
          variantId: String(
            productVariant ? productVariant.id : product.variants[0]?.id
          ),
        })

        if (window.gtag) {
          window.gtag('event', 'add_to_cart', {
            currency: productVariant.price.currencyCode || 'USD',
            value: productVariant.price.value,
            items: [
              {
                item_id: productVariant.sku,
                item_name: productVariant.name,
                affiliation: 'Yadav Diamonds and Jewelry',
                item_brand: 'Yadav Diamonds and Jewelry',
                item_category: product.taxons.find((taxon) => taxon.isLeaf)
                  ?.name,
                item_variant: productVariant.options[0]?.values[0]?.label,
                location_id: `${process.env.showroomPlaceId}`,
                price: productVariant.price.value,
                quantity: 1,
              },
            ],
          })
        }
        // Save ring size if selected
        if (selectedSizeValue && selectedSizeValue !== '') {
          localStorage.setItem('selected-ring-size', formattedSizeContent)
        }
        router.push('/cart')
      } catch (err) {
        setLoading(false)
      }
    }
  }

  const { price } = usePrice({
    amount: productVariant.price.value,
    baseAmount: productVariant.price.retailPrice,
    currencyCode: productVariant.price.currencyCode!,
    additional: additionalPriceBySize,
  })

  const { price: bankWirePrice } = usePrice({
    amount: calculateBankWirePrice(productVariant.price.value),
    currencyCode: productVariant.price.currencyCode!,
  })

  const handleClickProduct = () => {
    setSettingSelect(productVariant)
    // Save ring size if selected
    if (
      typeof window !== 'undefined' &&
      selectedSizeValue &&
      selectedSizeValue !== ''
    ) {
      localStorage.setItem('selected-ring-size', formattedSizeContent)
    }
  }

  useEffect(() => {
    if (sizeOptions.length > 0) {
      localStorage.setItem(
        `sizeOptions-${productVariant.id}`,
        JSON.stringify(sizeOptions)
      );
    }
  }, [sizeOptions, productVariant.id]);



  const saveSizeOnStorage = () => {
    if (productVariant) {
      const formattedSizeContent = JSON.stringify({
        variantId: productVariant.id,
        size: selectedSizeValue,
        additional: additionalPriceBySize,
      });
      localStorage.setItem('selected-ring-size', formattedSizeContent);
    }
  };

  const handleMouseEnterPrice = () => {
    if (!tooltipPriceInTransition) {
      setShowTooltipPrice(true)
    }
  }

  const handleMouseLeavePrice = () => {
    if (!tooltipPriceInTransition) {
      setShowTooltipPrice(false)
    }
  }

  const handleTouchStartPrice = () => {
    if (!tooltipPriceInTransition) {
      setShowTooltipPrice(true)
      setTooltipPriceInTransition(true)
    }
  }

  const handleTouchEndPrice = () => {
    if (tooltipPriceInTransition) {
      setTooltipPriceInTransition(false)
    } else {
      setShowTooltipPrice(false)
    }
  }

  const closeTooltipPrice = () => {
    setShowTooltipPrice(false)
    setTooltipPriceInTransition(false)
  }

  useEffect(() => {
    try {
      let diamondLocal = localStorage.getItem('diamond')
      if (diamondLocal) {
        let diamondParse = JSON.parse(diamondLocal)
        setDiamondInLocal(diamondParse)
      }
    } catch (error) {
      console.log(error)
    }

    {
      /*if (product.cyo_type === 'inquire-only') {
      setShowFullDescription(true)
    }*/
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function setOptionLabels(selectedOptions: SelectedOptions) {
    if (!selectedOptions) return null

    const metal = selectedOptions['metal']
    const tcw = selectedOptions['total carat weight']
    const diamType = selectedOptions['diamond type']

    if (!metal) return null

    if (tcw || diamType) {
      return (
        <span className="capitalize text-sm md:text-base font-normal">
          {`${metal} | ${tcw ? tcw + ' tw.' : ''}${diamType
              ? diamType === 'lab'
                ? ' Lab Grown Diamond'
                : ' Natural Diamond'
              : ''
            }`}
        </span>
      )
    } else {
      return (
        <span className="capitalize text-sm md:text-base font-normal">
          {`${metal}`}
        </span>
      )
    }
  }
  {
    /*const toggleDescription = () => {
    setShowFullDescription(!showFullDescription)
  }*/
  }
  const toggleDetails = () => {
    setShowFullDetails(!showFullDetails)
  }

  const detailsStyle = useSpring({
    //maxHeight: showFullDetails ? '100rem' : '4rem',
    config: { tension: 250, friction: 32, clamp: true },
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: '#636465',
    fontSize: '16px',
    fontWeight: 400,
  })

  const handleDetails = () => {
    let hasMetal = false
    let hasWidth = false
    let hasWeight = false
    let hasSKU = false
    if (productVariant && productVariant.sku) {
      hasSKU = true
    }
    if (
      productVariant.options.find((option) => option.displayName === 'Metal')
    ) {
      hasMetal = false
    }
    if (productVariant.width && productVariant.width !== '0.0') {
      hasWidth = true
    }
    if (productVariant.weight && productVariant.weight !== '0.0') {
      hasWeight = false
    }
    return (
      <ul>
        <li className="mb-2 font-normal text-base text-gray-dark leading-6 flex items-center gap-2">
          <div className="w-[5px] h-[5px] rounded-full bg-gray-dark"></div>{' '}
          <div className="flex text-gray-dark">
            Bank Wire Price{' '}
            <div className={s.hasTooltip}>
              <span
                className={cn(
                  s.tooltip,
                  'rounded shadow-lg p-2 bg-primary text-gray-dark text-xs border border-gray-light -mt-52 -ml-[92px]'
                )}
              >
                A bank wire can be used to pay for Yadav Diamonds and Jewelry.
                Yadav Jewelry applies a 2.0% discount to your order when you use
                a bank wire. Please Note: Some financial institutions charge a
                fee for using a bank wire. If you have any questions about the
                steps involved in a bank wire, contact your bank or call Yadav
                Diamonds and Jewelry customer service at 1-888 968 8810
              </span>
              <FontAwesomeIcon
                icon={faCircleQuestion}
                className="text-accent-9 ml-1 mr-0.5 cursor-pointer"
              />
            </div>
            <span>{`: ${bankWirePrice}`}</span>
          </div>
        </li>
        {hasWidth && (
          <li className="mb-2 font-normal text-base text-gray-dark capitalize leading-6 flex items-center">
            <span className="font-normal text-base text-gray-dark flex gap-2 items-center">
              <div className="w-[5px] h-[5px] rounded-full bg-gray-dark"></div>{' '}
              Width: {productVariant?.width} MM
            </span>
          </li>
        )}
        {hasMetal && (
          <li className="mb-2 font-normal text-base text-gray-dark capitalize leading-6 flex items-center">
            <span className="font-normal text-base text-gray-dark flex gap-2 items-center">
              <div className="w-[5px] h-[5px] rounded-full bg-gray-dark"></div>{' '}
              Metal: {productVariant?.options[0]?.values[0]?.label}
            </span>
          </li>
        )}

        {hasWeight && (
          <li className="mb-2 font-normal text-base text-gray-dark capitalize leading-6 flex items-center">
            <span className="font-normal text-base text-gray-dark flex gap-2 items-center">
              <div className="w-[5px] h-[5px] rounded-full bg-gray-dark"></div>{' '}
              Weight: {productVariant?.weight}
            </span>
          </li>
        )}
        {hasSKU && (
          <li className="mb-2 font-normal text-base text-gray-dark capitalize leading-6 flex items-center">
            {' '}
            <span className="font-normal text-base text-gray-dark flex gap-2 items-center">
              <div className="w-[5px] h-[5px] rounded-full bg-gray-dark"></div>
              SKU: {productVariant?.sku}
            </span>
          </li>
        )}
      </ul>
    )
  }

  return (
    <>
      <div className="lg:sticky lg:top-[20px]">
        <div className="mt-7 lg:ml-auto lg:max-w-[401px]">
          {productVariant && (
            <div className="flex flex-col gap-4 md:items-center lg:items-start">
              <div className="flex flex-row items-center justify-between sm:justify-start gap-4">
                <h1 className="capitalize font-normal text-2xl md:text-3.5xl">
                  {product.name}
                </h1>
                {process.env.COMMERCE_WISHLIST_ENABLED && (
                  <WishlistButton
                    productId={product.id}
                    variantId={productVariant.id}
                    className={s.iconHeart}
                  />
                )}
              </div>
              {setOptionLabels(selectedOptions)}
            </div>
          )}
          <div>
            <div className="flex my-6 md:justify-center lg:justify-start">
              <div>
                {productVariant?.cyoType === 'inquire-only' ? (
                  <div className="text-xs md:text-base text-gray-dark">
                    <span className="block lg:hidden">Price from</span>
                    <span className="hidden lg:block">Starting at</span>
                  </div>
                ) : (
                  <div className="text-xs md:text-base text-gray-dark">
                    {`Price${productVariant?.cyoType === 'setting'
                      ? ' (Only Setting)'
                      : ''
                    }`}
                  </div>
                )}
                {/* Precio */}
                <span className={cn(
                    s.priceCart,
                    !productVariant.availableForSale && 'line-through opacity-50'
                  )}
                >
                  {price}
                </span>
              </div>
            </div>
            <ProductOptions
              product={product}
              productVariant={productVariant}
              options={product.options}
              setSelectedOptions={setSelectedOptions}
              className="mb-2"
              type={'METAL'}
              setVariantSettings={setVariantSettings}
              variantSettings={variantSettings}
              productType={product.productType}
            />
            <ProductOptions
              setSelectedOptions={setSelectedOptions}
              product={product}
              productVariant={productVariant}
              options={product.options}
              className="mb-2"
              type={'DIAMOND_TYPE'}
              setVariantSettings={setVariantSettings}
              variantSettings={variantSettings}
              productType={product.productType}
            />
            <ProductOptions
              setSelectedOptions={setSelectedOptions}
              type={'TCW'}
              options={product.options}
              productVariant={productVariant}
              product={product}
              className="mb-2"
              productType={product.productType}
              setVariantSettings={setVariantSettings}
              variantSettings={variantSettings}
            />
            <ProductOptions
              setSelectedOptions={setSelectedOptions}
              type={'SIZE'}
              options={product.options}
              sizeOptions={sizeOptions}
              productVariant={productVariant}
              product={product}
              className="mb-2"
              productType={product.productType}
              setVariantSettings={setVariantSettings}
              variantSettings={variantSettings}
              selectedSizeValue={selectedSizeValue}
              setSelectedSizeValue={setSelectedSizeValue}
            />
            <div>
              {process.env.COMMERCE_CART_ENABLED && cyoType === 'standalone' && (
                <div className="w-full lg:mx-0">
                  <Button
                    aria-label="Add to Cart"
                    type="button"
                    variant="type2"
                    width="100%"
                    onClick={() => {
                      saveSizeOnStorage()
                      addToCart()
                    }}
                    loading={loading}
                    disabled={!productVariant.availableForSale}
                  >
                    {!productVariant.availableForSale
                      ? 'Not Available'
                      : 'Add To Cart'}
                  </Button>
                </div>
              )}
              {process.env.COMMERCE_CART_ENABLED && cyoType === 'setting' && (
                <div className="w-full lg:mx-0">
                  {diamondInLocal ? (
                    <Button
                      aria-label="Choose this setting"
                      type="button"
                      variant="type2"
                      width="100%"
                      onClick={() => {
                        handleClickProduct()
                        setLoadingReview(!loadingReview)
                        router.push('/create/review')
                      }}
                      loading={loadingReview}
                      disabled={!productVariant.availableForSale}
                    >
                      {!productVariant.availableForSale
                        ? 'Not Available'
                        : 'Choose this Setting'}
                    </Button>
                  ) : (
                    <Button
                      aria-label="Choose this setting"
                      type="button"
                      variant="type2"
                      width="100%"
                      onClick={() => {
                        setDisplayModal(true)
                      }}
                      disabled={!productVariant.availableForSale}
                    >
                      {!productVariant.availableForSale
                        ? 'Not Available'
                        : 'Choose this Setting'}
                    </Button>
                  )}
                </div>
              )}

              {process.env.COMMERCE_CART_ENABLED && cyoType === 'gemstone' && (
                <div className="w-full lg:mx-0">
                  <Button
                    aria-label="Choose this diamond"
                    type="button"
                    variant="type2"
                    width="100%"
                    onClick={() => {
                      saveSizeOnStorage()
                      addToCart()
                    }}
                    loading={loading}
                    disabled={!productVariant.availableForSale}
                  >
                    {!productVariant.availableForSale
                      ? 'Not Available'
                      : 'Choose this Diamond'}
                  </Button>
                </div>
              )}

              {process.env.COMMERCE_CART_ENABLED && cyoType === 'inquire-only' && (
                <div className="w-full lg:mx-0">
                  <Button
                    aria-label="Inquire about this product"
                    type="button"
                    variant="type2"
                    width="100%"
                    onClick={() => setDisplayInquireOnlyModal(true)}
                  >
                    {!productVariant.availableForSale
                      ? 'Not Available'
                      : 'Inquire About this Product'}
                  </Button>
                </div>
              )}
            </div>
            {product.description && (
              <>
                <div className="mt-7 mb-3.5 text-base font-bold leading-6">
                  Description:
                </div>
                <div className="flex flex-col items-start sm:max-w-[401px] max-w-[348px] gap-2">
                  <Text
                    html={product.description}
                    className={cn(
                      s.description,
                      '!text-gray-dark',
                      '!text-base',
                      'h-auto'
                    )}
                  ></Text>
                  {/*{!showFullDescription ? (
                    <button
                      onClick={toggleDescription}
                      className="underline text-gray-dark text-base hover:text-primary"
                    >
                      Read More
                    </button>
                  ) : (
                    <button
                      onClick={toggleDescription}
                      className="underline text-gray-dark text-base hover:text-primary"
                    >
                      Read Less
                    </button>
                  )}*/}
                </div>
              </>
            )}
            <div className="mt-7 mb-3.5 text-base font-bold leading-6">
              Details:
            </div>
            <a.div style={detailsStyle}>{handleDetails()}</a.div>
            <div>
              {product.cyo_type === 'setting' &&
                product.canBeSetWith &&
                product.canBeSetWith.length > 0 && (
                  <div className="mb-4">
                    <div className="my-7 text-base font-bold leading-6">
                      This Ring Can Be Set With:
                    </div>
                    <div className="grid grid-cols-5 sm:grid-cols-8 lg:grid-cols-5 gap-x-2 gap-y-6">
                      {product.canBeSetWith.map((shape) => (
                        <div
                          key={shape}
                          className="flex flex-col gap-1 items-center text-center"
                        >
                          {canBeSetWithShapes[
                            shape as keyof typeof canBeSetWithShapes
                          ]()}
                          <span className="text-xs text-gray-dark capitalize md:text-sm md:leading-5 leading-4">
                            {shape.replace('_', ' ')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
            <div className="mb-3.5">
              <AppointmentButtons variant="person" />
            </div>
            <div className="mb-3.5">
              <AppointmentButtons variant="virtual" />
            </div>
            {product.properties &&
              product.properties.length > 0 &&
              product.properties.some(
                (property) =>
                  property.name.toLowerCase() === 'augmented-reality' &&
                  property.value?.toLowerCase() === 'yes'
              ) && (
                <>
                  <div className="mb-3.5">
                    <AppointmentButtons
                      variant="inova"
                      idInova="try_vto_3"
                      hidden
                    />
                  </div>
                  <div>
                    <AppointmentButtons
                      variant="inova"
                      idInova="try_vto_4"
                      hidden
                    />
                  </div>
                </>
              )}
          </div>
        </div>
      </div>
      {displayModal && (
        <Modal onClose={() => setDisplayModal(false)}>
          <PurchaseOptionsView productVariant={productVariant} />
        </Modal>
      )}
      {displayInquireOnlyModal && (
        <Modal
          onClose={() => setDisplayInquireOnlyModal(false)}
          childrenClassName="lg:w-[708px]"
        >
          <InquireOnlyProductForm
            productSku={
              productVariant.sku ||
              `${productVariant.name} - ${productVariant.options[0]?.values[0]?.label} (SKU undefined)`
            }
          />
        </Modal>
      )}
    </>
  )
}
export default ProductDescription
